import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { requestSuggestion } from "../../abstract/api/rest";
import * as nodeUtils from "../../abstract/controller/utils/nodeUtils";
import useRequestContainer from "../../abstract/hook/useRequestContainer";
import { getEndpointAI } from "../../abstract/redux/slice/endpointSlice";
import { handleNodeUpdateLabel } from "../../abstract/redux/slice/structureSlice";
import IconInfoCircle from "../../themes/icons/IconInfoCircle";
import TooltipIconLabel from "../tooltip/TooltipIconLabel";
import TooltipLabel from "../tooltip/TooltipLabel";
import * as vendorTooltip from "../ui/tooltips/DefaultTooltip";
import styles from "./ElementLabel.module.scss";

const _placeholderSuggestion = "Loading suggestion...";
const _placeholderDefault = "Labels";

function ElementLabel({ node }) {
  const _dispatch = useDispatch();

  const _endpointAI = useSelector(getEndpointAI);

  const _id = nodeUtils.getID(node) ?? "";
  const _uuid = nodeUtils.getID(node) ?? "";
  const _labels = nodeUtils.getLabel(node);

  const _container = useRequestContainer({ id: _id, nodeParent: node, type: "label" });

  const [_optionLabel, _setOptionLabel] = useState(_labels);
  const [_isLoadingSuggestion, _setIsLoadingSuggestion] = useState(false);

  const [_placeholder, _setPlaceholder] = useState(_placeholderDefault);

  const _labelRef = useRef(null);
  const _suggestionTimeoutRef = useRef(null);
  const _abortRef = useRef(null);

  const clearSuggestionTimeout = useCallback(() => {
    if (_suggestionTimeoutRef.current) {
      clearTimeout(_suggestionTimeoutRef.current);
      _suggestionTimeoutRef.current = null;
    }

    _setIsLoadingSuggestion(false);
  }, []);

  const actionChangeLabelAccept = useCallback(() => {
    clearSuggestionTimeout();

    if (_optionLabel.length > 0 && _optionLabel[0] !== "") {
      _dispatch(handleNodeUpdateLabel(_id, _optionLabel));
    } else {
      _dispatch(handleNodeUpdateLabel(_id, null));
    }
  }, [clearSuggestionTimeout, _dispatch, _id, _optionLabel]);

  const actionChangeLabelCancel = useCallback(() => {
    clearSuggestionTimeout();
    _setPlaceholder(_placeholderDefault);
  }, [clearSuggestionTimeout]);

  const handleInputLabelOnChange = (e) => {
    const _value = e.target.value;

    _setOptionLabel(_value.split(","));
    _setPlaceholder(_placeholderDefault);

    if (_value && _value.trim() !== "") {
      if (_labelRef.current.selectionEnd >= _labelRef.current.textLength) {
        suggestLabelSuffix(_value);
      } else {
        clearSuggestionTimeout();
      }
    }
  };

  const handleInputLabelFocusIn = () => {
    if (_isLoadingSuggestion) {
      return;
    }

    if (_optionLabel.length === 0) {
      suggestLabel();
    } else {
      _labelRef.current.select();
    }
  };

  const handleInputLabelFocusOut = () => {
    if (_isLoadingSuggestion) {
      return;
    }

    clearSuggestionTimeout();

    if (_optionLabel.length > 0 && _optionLabel[0] !== "") {
      _dispatch(handleNodeUpdateLabel(_id, _optionLabel));
    } else {
      _dispatch(handleNodeUpdateLabel(_id, null));
    }
  };

  const suggestLabel = useCallback(async () => {
    if (!_endpointAI) {
      return;
    }
    
    _setIsLoadingSuggestion(true);
    _setPlaceholder(_placeholderSuggestion);

    _abortRef.current = new AbortController();

    try {
      const _suggestion = await requestSuggestion({
        controller: _abortRef.current,
        ..._container.request,
      });

      if (_labelRef.current) {
        if (_suggestion.status !== "error" && !_labelRef.current.value) {
          _setOptionLabel(_suggestion.message.split(","));

          _labelRef.current.value = _suggestion.message;
          _labelRef.current.select();
        } else {
          _setPlaceholder("No available suggestions");
        }
      }
    } catch (exception) {}

    clearSuggestionTimeout();
  }, [_container.request, _endpointAI, clearSuggestionTimeout]);

  const suggestLabelSuffix = useCallback(
    async (prefix) => {
      if (!_endpointAI) {
        return;
      }
      if (_suggestionTimeoutRef.current) {
        return;
      }

      _abortRef.current = new AbortController();

      _suggestionTimeoutRef.current = setTimeout(async () => {
        _setIsLoadingSuggestion(true);

        try {
          const _suggestion = await requestSuggestion({
            controller: _abortRef.current,
            prefix,
            ..._container.request,
          });

          if (_suggestion.status !== "error") {
            _setOptionLabel(_suggestion.message.split(","));

            if (_labelRef.current) {
              _labelRef.current.value = _suggestion.message;
              _labelRef.current.setSelectionRange(prefix.length, _suggestion.message.length);
            }
          } else {
            _setPlaceholder("No available suggestions");
          }
        } catch (error) {}

        clearSuggestionTimeout();
      }, 2000);
    },
    [_container.request, _endpointAI, clearSuggestionTimeout]
  );

  useEffect(() => {
    const _target = _labelRef.current;

    const callback = (e) => {
      if (e.code === "Enter") {
        if (!_isLoadingSuggestion) {
          actionChangeLabelAccept();
        }
      } else if (e.code === "Escape") {
        actionChangeLabelCancel();
      } else if (e.code === "ArrowRight") {
        if (_target.selectionEnd + 1 >= _target.textLength) {
          suggestLabelSuffix(_target.defaultValue);
        }
      } else {
        clearSuggestionTimeout();
      }
    };

    _target?.addEventListener("keydown", callback);

    return () => _target?.removeEventListener("keydown", callback);
  }, [
    _isLoadingSuggestion,
    actionChangeLabelAccept,
    actionChangeLabelCancel,
    clearSuggestionTimeout,
    suggestLabelSuffix,
  ]);

  useEffect(() => {
    return () => {
      if (_suggestionTimeoutRef.current) {
        clearTimeout(_suggestionTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className={styles["meta__option"]}>
      <div className={styles["meta__option-header"]}>
        <div className={styles["meta__option-header-name"]}>{`Labels (${_labels.length})`}</div>
        <div className={styles["meta__option-header-icon"]}>
          <IconInfoCircle
            data-tooltip-id={`meta-tooltip-label-${_uuid}`}
            data-tooltip-delay-show={vendorTooltip.getTimeFast()}
          />
          <TooltipIconLabel handle={`meta-tooltip-label-${_uuid}`} />
        </div>
      </div>
      <input
        ref={_labelRef}
        type="text"
        value={_optionLabel.join(",")}
        onChange={handleInputLabelOnChange}
        onFocus={handleInputLabelFocusIn}
        onBlur={handleInputLabelFocusOut}
        className={`${styles["meta__option-input"]} ${_isLoadingSuggestion ? styles.shimmer : ""}`}
        data-title={_optionLabel.join(",")}
        data-tooltip-id={`meta-label-${_uuid}`}
        data-tooltip-delay-show={vendorTooltip.getTimeFast()}
        placeholder={_placeholder}
      />
      <TooltipLabel node={node} handle={`meta-label-${_uuid}`} />
    </div>
  );
}

export default ElementLabel;
